import React, { useEffect } from 'react';
import { css } from '@emotion/css';
import Button from '../../../../components/general/Button';
import ConfirmationDialog from '../../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../../components/general/WarningConfirmation';
import { color_shades_dark, color_variants_shadow_10, dark_grey_blue_70 } from '../../../../constants/colors';
import ApplicationModel from '../../../../models/applications/api/ApplicationModel';
import StyledDropdown from '../../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import styled from '@emotion/styled';
import { breakpoint_small } from '../../../../constants/breakpoints';
import { getSparkMainBaseUrl } from '../../../../constants/Urls';
import { getJobberClientId } from '../../../../constants/integrationClientIds';
import { getJobberConnectedApplications } from '../../../../api/controllers/businessManagement';
import ConnectedAppResponse from '../../../../models/integrations/api/Salesforce/ConnectedAppResponse';

interface JobberIntegrationProps {
    applications: ApplicationModel[];
    isLoading: boolean;
    organizationId: string;
}

const JobberIntegration: React.FC<JobberIntegrationProps> = ({ applications, isLoading, organizationId }) => {
    const [showJobberModal, setShowJobberModal] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState('');
    const [connectedApps, setConnectedApps] = React.useState<ConnectedAppResponse[]>([]);
    const [isLoadingConnectedApps, setIsLoadingConnectedApps] = React.useState(false);

    useEffect(() => {
        const fetchConnectedApps = async () => {
            if (!organizationId || !applications.length) return;

            setIsLoadingConnectedApps(true);
            try {
                const applicationIds = applications
                    .map(app => app.id)
                    .filter(id => id && id.trim());

                const result = await getJobberConnectedApplications(organizationId, applicationIds);

                if (result.resultType === "Ok" && result.data) {
                    setConnectedApps(result.data);
                }
            } catch (error) {
                console.error('Error fetching connected applications:', error);
            }
            setIsLoadingConnectedApps(false);
        };

        fetchConnectedApps();
    }, [organizationId, applications]);

    const getRedirectUri = () => {
        return `${getSparkMainBaseUrl()}/v/orgSettings`;
    }

    const handleJobberConnect = () => {
        const redirectUri = getRedirectUri();
        const clientId = getJobberClientId();
        window.location.href = `https://api.getjobber.com/api/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=Jobber-${selectedLocation}`;
    };

    const isAppConnected = (appId: string) => {
        return connectedApps.some(connectedApp => connectedApp.applicationId === appId);
    };

    const locationOptions = applications.map(app => ({
        value: app.id,
        label: isAppConnected(app.id) ? `${app.name} - ACCOUNT CONNECTED` : app.name
    }));

    return (
        <>
            <div className={integrationCardStyle}>
                <div className={integrationCardContentStyle}>
                    <h2>Jobber Integration</h2>
                    <p>Jobber is an award-winning business management platform for small home service businesses.</p>
                    <p>Connect to Jobber to give your assistant access to client and customer information.</p>
                    <div className={dropdownContainerStyle}>
                        <CustomDropdown
                            disabled={isLoading || isLoadingConnectedApps}
                            handleOnChange={(_, value) => setSelectedLocation(value as string)}
                            options={locationOptions}
                            value={selectedLocation}
                            placeholder={isLoadingConnectedApps ? "Loading connected apps..." : "Select a location"}
                            size="medium"
                        />
                    </div>
                </div>
                <Button
                    themes={["primary"]}
                    text="Authenticate"
                    className={authenticateButtonStyle(selectedLocation === '')}
                    onClick={() => selectedLocation && setShowJobberModal(true)}
                />
            </div>
            {showJobberModal && (
                <ConfirmationDialog
                    title="Connect to Jobber"
                    onClose={() => setShowJobberModal(false)}
                    onConfirm={handleJobberConnect}
                    deleteText="Authenticate"
                >
                    <WarningConfirmation
                        showImage={false}
                        text="By authenticating to Jobber, you grant access to your client and customer information. This integration helps provide better service through voice interactions."
                    />
                </ConfirmationDialog>
            )}
        </>
    );
};

const integrationCardStyle = css`
    border: 1px solid ${color_shades_dark};
    display: flex;
    margin-top: 10px;
    justify-content: space-between;
    border-radius: 8px;
    padding: 24px;
    background: white;
    box-shadow: 0 2px 4px 0 ${color_variants_shadow_10};

    h2 {
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: 600;
    }
    
    p {
        margin-bottom: 6px;
        font-size: 14px;
        color: ${dark_grey_blue_70};
    }

    ${breakpoint_small} {
        margin-top: 5px;
        display: block;
    }
`;

const integrationCardContentStyle = css`
    width: 50%;
    ${breakpoint_small} {
        width: 100%;
    }
`;

const dropdownContainerStyle = css`
    margin-top: 16px;
`;

const authenticateButtonStyle = (disabled: boolean) => css`
    align-self: center;
    ${disabled && css`
        opacity: 0.5;
        cursor: not-allowed;
    `}
`;

const CustomDropdown = styled(StyledDropdown)`
    padding: 5px;
    margin: 0 0 24px 0;
    
    .Select-menu-outer {
        max-height: none !important;
        z-index: 10001;
        padding: 10px 0px;
    }
    
    .Select-menu {
        max-height: 300px;
    }
`;

export default JobberIntegration;
